if (!customElements.get('aurastep-benefits')) {
  customElements.define('aurastep-benefits', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.track = this.querySelector('.aurastep-benefits__track');
      this.cards = [...this.querySelectorAll('.aurastep-benefits__card')];
      this.toggle = this.querySelector('.aurastep-benefits__autoplay');
      if (!this.track || !this.cards.length) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.mobile = matchMedia('(max-width: 749px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.pointerActive = false;
      this.focused = false;
      this.inView = false;
      this.dataset.enhanced = '';
      this.track.setAttribute('aria-label', 'Avantages Aurastep');
      this.toggle?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.allowToggleFocus = !this.userPaused;
        this.syncAutoplay();
      }, { signal });
      this.track.addEventListener('scroll', () => {
        this.stopTimer();
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { passive: true, signal });
      // A touch or swipe delays rotation; only the explicit control pauses it persistently.
      this.track.addEventListener('pointerdown', () => {
        this.pointerActive = true;
        this.focused = false;
        this.syncAutoplay();
      }, { passive: true, signal });
      const releasePointer = () => {
        if (!this.pointerActive) return;
        this.pointerActive = false;
        this.stopTimer();
        this.syncAutoplay();
      };
      document.addEventListener('pointerup', releasePointer, { passive: true, signal });
      document.addEventListener('pointercancel', releasePointer, { passive: true, signal });
      this.track.addEventListener('wheel', (event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
          this.stopTimer();
          this.syncAutoplay();
        }
      }, { passive: true, signal });
      this.track.addEventListener('keydown', (event) => {
        if (!this.scrollable || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.focused = true;
        this.stopTimer();
        const target = event.key === 'Home' ? 0 : event.key === 'End' ? this.cards.length - 1 : this.active + (event.key === 'ArrowRight' ? 1 : -1);
        this.goTo(Math.max(0, Math.min(this.cards.length - 1, target)));
      }, { signal });
      this.addEventListener('focusin', () => {
        this.focused = Boolean(document.activeElement?.matches(':focus-visible'));
        this.allowToggleFocus = false;
        this.syncAutoplay();
      }, { signal });
      this.addEventListener('focusout', (event) => {
        this.focused = this.contains(event.relatedTarget) && Boolean(event.relatedTarget?.matches(':focus-visible'));
        this.syncAutoplay();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pointerActive = false;
        this.syncAutoplay();
      }, { signal });
      this.mobile.addEventListener('change', () => this.update(), { signal });
      this.reducedMotion.addEventListener('change', () => this.syncAutoplay(), { signal });
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.track);
      this.visibility = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.35;
        if (this.inView) this.cards.forEach((card) => {
          const image = card.querySelector('img');
          if (image) image.loading = 'eager';
        });
        this.syncAutoplay();
      }, { threshold: [0, 0.35] });
      this.visibility.observe(this);
      this.update();
    }
    offset(index) {
      return this.cards[index].offsetLeft - this.cards[0].offsetLeft;
    }
    goTo(index) {
      this.track.scrollTo({ left: this.offset(index), behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
    }
    stopTimer() {
      clearTimeout(this.timer);
      this.timer = null;
    }
    syncAutoplay() {
      if (!this.events) return;
      const enabled = this.mobile.matches && this.scrollable && !this.reducedMotion.matches;
      if (this.toggle) {
        this.toggle.hidden = !enabled;
        this.toggle.setAttribute('aria-pressed', String(this.userPaused));
        this.toggle.setAttribute('aria-label', this.userPaused ? 'Reprendre le défilement automatique' : 'Mettre en pause le défilement automatique');
        this.toggle.querySelector('[data-autoplay-pause]').hidden = this.userPaused;
        this.toggle.querySelector('[data-autoplay-play]').hidden = !this.userPaused;
      }
      const focusBlocks = this.focused && !(this.allowToggleFocus && document.activeElement === this.toggle);
      const running = enabled && this.inView && !document.hidden && !this.userPaused && !this.pointerActive && !focusBlocks;
      this.dataset.autoplay = running ? 'running' : 'paused';
      this.dataset.autoplayReason = running ? '' : !enabled ? 'disabled' : !this.inView ? 'offscreen' : document.hidden ? 'hidden' : this.userPaused ? 'user' : this.pointerActive ? 'pointer' : 'keyboard';
      this.track.setAttribute('aria-live', running ? 'off' : 'polite');
      if (!running) {
        this.stopTimer();
      } else if (!this.timer) {
        this.timer = setTimeout(() => {
          this.timer = null;
          if (this.dataset.autoplay !== 'running') return;
          this.goTo((this.active + 1) % this.cards.length);
          this.syncAutoplay();
        }, 5000);
      }
    }
    update() {
      this.scrollable = this.mobile.matches && this.track.scrollWidth > this.track.clientWidth + 2;
      this.track.tabIndex = this.scrollable ? 0 : -1;
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.active = this.scrollable ? this.cards.reduce((closest, card, index) =>
        Math.abs(this.track.scrollLeft - Math.min(max, this.offset(index))) < Math.abs(this.track.scrollLeft - Math.min(max, this.offset(closest))) ? index : closest, 0) : 0;
      this.cards.forEach((card, index) => {
        if (this.scrollable) card.setAttribute('aria-current', String(index === this.active));
        else card.removeAttribute('aria-current');
      });
      this.syncAutoplay();
    }
    disconnectedCallback() {
      this.stopTimer();
      this.events?.abort();
      this.events = null;
      this.resize?.disconnect();
      this.visibility?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}
